import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setRestaurants: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	selectedCustomer: Nullable<string>;
	selectedRestaurants: string[];
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	selectedCustomer,
	selectedRestaurants,
	setRestaurants,
	setFetchedRestaurants,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		selectedCustomer ? getRestaurantsShortInfo : null,
		{
			customerId: selectedCustomer || '',
			menuApiVersion: 'v1',
		},
	);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = selectedRestaurants.filter((el) => el !== id);
			setRestaurants(updatedSelectedItems);
		},
		[selectedRestaurants, setRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return selectedRestaurants.map((el) => {
			const label = response?.data.restaurants.find((item) => item.id === el)?.name || '';
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)}
				/>
			);
		});
	}, [selectedRestaurants, response, loggedUser, hasAccess, handleRemove]);

	useEffect(() => {
		refetch();
	}, [selectedCustomer]);

	useEffect(() => {
		if (response) {
			const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
			const restaurants = hasAccess ? response.data.restaurants : accessibleRestaurants;
			setFetchedRestaurants(restaurants);
		}
	}, [response, loggedUser, hasAccess]);

	return (
		<div className={style.container} data-test="RestaurantsMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.restaurants || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					selectedCustomer={selectedCustomer}
					selectedRestaurants={selectedRestaurants}
					setRestaurants={setRestaurants}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
